import { useEffect } from 'react';
import { api, queryClient } from '../api.js';
import { isNetworkError, useOffline } from '../../stores/offline.js';
import { can, useSession } from '../../stores/session.js';
import { refreshCustomers } from './cache.js';
import { claimDb, kvSet } from './db.js';
import { refreshHolds } from './holds.js';
import { loadPermitState, refreshPermit } from './permit.js';
import { markOfflineSession, rememberForReconnect, rememberPinSignIn, takeReconnectPin } from './staff.js';
import { flushOutbox, refreshCounts, startOfflineSync } from './sync.js';

export { completeOffline, flushOutbox, outboxItems, queueKitchenUpdate, refreshCounts, retryFailed } from './sync.js';
export { offlineCheck } from './permit.js';

/**
 * Keeps this device ready to work offline while it's online: caches the signed-in profile and
 * device, and runs the sync loop (bills, open orders, kitchen screen taps). Registered billing
 * devices (pos.bill) also refresh the offline permit, the recent customers and the open orders.
 */
export function useOfflineReady() {
  const me = useSession((s) => s.me);
  const device = useSession((s) => s.device);
  const reachable = useOffline((s) => s.reachable);
  const registered = Boolean(me?.device?.id);
  const billing = registered && can(me, 'pos.bill');

  useEffect(() => {
    if (!me) return;
    claimDb(me.tenant.id).then(() => {
      kvSet('me', me);
      if (device) kvSet('device', device);
    });
  }, [me, device]);

  useEffect(() => {
    if (!registered) return undefined;
    refreshCounts();
    return startOfflineSync();
  }, [registered]);

  useEffect(() => {
    if (billing) loadPermitState();
  }, [billing]);

  useEffect(() => {
    if (!billing || !reachable) return undefined;
    const refresh = () => Promise.all([refreshPermit(), refreshCustomers(), refreshHolds()]).then(() => flushOutbox());
    refresh();
    const t = setInterval(refresh, 30 * 60_000);
    return () => clearInterval(t);
  }, [billing, reachable]);
}

/**
 * Someone switched in offline (PIN checked on this device): once the server is reachable, sign
 * them in for real so everything they do online is theirs. With the PIN from memory when the
 * app wasn't reloaded meanwhile; otherwise the PIN screen asks again.
 */
export function useOnlineResume() {
  const offline = useSession((s) => s.offline);
  const reachable = useOffline((s) => s.reachable);
  useEffect(() => {
    if (!offline || !reachable) return;
    let cancelled = false;
    (async () => {
      const me = useSession.getState().me;
      const pin = takeReconnectPin();
      try {
        // The server session may already be this person's (they switched back to themselves).
        const server = await api.get('/auth/me').catch((err) => (isNetworkError(err) ? Promise.reject(err) : null));
        if (cancelled) return;
        if (server?.user?.id === me.user.id) {
          useSession.getState().signedIn(server);
        } else if (pin?.userId === me.user.id) {
          const res = await api.post('/auth/pin-login', { userId: pin.userId, pin: pin.pin });
          useSession.getState().signedIn(res.me);
          void rememberPinSignIn(res.me, pin.pin);
        } else {
          throw Object.assign(new Error('PIN needed'), { needPin: true });
        }
        await markOfflineSession(false);
      } catch (err) {
        if (cancelled) return;
        if (isNetworkError(err)) {
          if (pin) rememberForReconnect(pin.userId, pin.pin); // try again on the next reconnect
          useOffline.getState().set({ reachable: false });
          return;
        }
        // Wrong PIN now (changed meanwhile), locked, or the PIN is no longer in memory.
        await api.post('/auth/logout').catch(() => {});
        await markOfflineSession(false);
        queryClient.clear();
        useSession.getState().signedOut('offlineEnded');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [offline, reachable]);
}
